import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileDown, Handshake, ArrowRight, ChevronDown, Users, SlidersHorizontal, Plus, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";
import { getGuest } from "@/lib/guest";
import { trackEvent } from "@/lib/settings";
import { usePageContent } from "@/lib/content";
import { mergeST } from "@/lib/staffingContent";
import { computeStaffing } from "@/lib/staffingEngine";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const num = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));
const N = (v) => (v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v));

function Num({ label, value, onChange, prefix, suffix, testId, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 border border-ink/15 bg-white px-3 py-2.5 focus-within:border-ink transition-colors">
        {prefix && <span className="text-sm text-graphite shrink-0">{prefix}</span>}
        <input type="number" min="0" value={value} placeholder="0"
          onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
          className="w-full bg-transparent outline-none text-sm" data-testid={testId} />
        {suffix && <span className="text-[11px] text-graphite whitespace-nowrap shrink-0">{suffix}</span>}
      </div>
      {hint && <span className="text-[11px] text-graphite/70 mt-1 block">{hint}</span>}
    </label>
  );
}

const TONES = { sky: "bg-sky-50 text-sky-700 border-sky-200", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", amber: "bg-amber-50 text-amber-700 border-amber-200", red: "bg-red-50 text-red-700 border-red-200" };

function DeptRow({ d, showFormulas, adjustMode, onOv, onRemove }) {
  const [open, setOpen] = useState(false);
  const isOpen = open || adjustMode;
  return (
    <div className="border border-ink/10 bg-white" data-testid={`sc-dept-${d.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bone/60 transition-colors" data-testid={`sc-dept-toggle-${d.id}`}>
        <span className="text-sm flex-1">{d.label}</span>
        <span className="text-sm tabular-nums w-14 text-right font-medium">{num(d.headcount)}</span>
        <span className="text-sm tabular-nums w-28 text-right hidden sm:block">{inr(d.monthly)}</span>
        <span className="text-sm tabular-nums w-28 text-right hidden md:block text-graphite">{inr(d.annual)}</span>
        <ChevronDown className={`w-4 h-4 text-graphite/50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="border-t border-ink/5 px-4 py-3 bg-bone/40 text-xs space-y-1.5">
          {showFormulas && <div className="text-graphite/80 mb-2"><span className="uppercase tracking-wide text-[10px] font-medium text-clay">Formula</span> · {d.formula}</div>}
          {d.roles.map((r) => (
            adjustMode ? (
              <div key={r.key} className="flex flex-wrap items-center gap-2 py-1" data-testid={`sc-adj-row-${d.id}-${r.id}`}>
                <span className="flex-1 min-w-[140px] text-graphite">{r.label}{r.overridden && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-clay">edited</span>}</span>
                <label className="flex items-center gap-1.5 text-graphite/70">
                  Count
                  <input type="number" min="0" value={r.count} onChange={(e) => onOv(r.key, "count", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                    className="w-16 border border-ink/15 bg-white px-2 py-1.5 outline-none focus:border-ink tabular-nums" data-testid={`sc-adj-count-${d.id}-${r.id}`} />
                </label>
                <label className="flex items-center gap-1.5 text-graphite/70">
                  ₹/mo gross
                  <input type="number" min="0" value={r.gross} onChange={(e) => onOv(r.key, "salary", e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                    className="w-24 border border-ink/15 bg-white px-2 py-1.5 outline-none focus:border-ink tabular-nums" data-testid={`sc-adj-salary-${d.id}-${r.id}`} />
                </label>
                <span className="tabular-nums text-graphite w-24 text-right">{inr(r.monthly)}/mo</span>
                <button type="button" onClick={() => onRemove(r.key)} className="p-1.5 text-graphite/50 hover:text-red-600 transition-colors" title="Remove this role" data-testid={`sc-adj-remove-${d.id}-${r.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div key={r.key || r.id} className="flex justify-between text-graphite">
                <span>{r.label} × {num(r.count)} @ {inr(r.loaded)}/mo loaded{r.overridden ? " (edited)" : ""}</span>
                <span className="tabular-nums">{inr(r.monthly)}/mo</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function CalculatorBody({ cfg }) {
  const [params] = useSearchParams();
  const [propertyName, setPropertyName] = useState(params.get("name") || "");
  const [rooms, setRooms] = useState("");
  const [seats, setSeats] = useState("");
  const [outdoor, setOutdoor] = useState("");
  const [gates, setGates] = useState("");
  const [actualCapacity, setActualCapacity] = useState("");
  const [complexityId, setComplexityId] = useState("standard");
  const [eventGuests, setEventGuests] = useState("");
  const [eventsPerYear, setEventsPerYear] = useState("");
  const [daysPerEvent, setDaysPerEvent] = useState(1);
  const [revenue, setRevenue] = useState(N(params.get("r")) || "");
  const [guestOpen, setGuestOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [adjustMode, setAdjustMode] = useState(false);
  const [adjust, setAdjust] = useState({ roles: {}, removed: [], added: [] });
  const [newRole, setNewRole] = useState(null);

  const hasAdjust = Object.keys(adjust.roles).length > 0 || adjust.removed.length > 0 || adjust.added.length > 0;
  const res = useMemo(() => computeStaffing({ rooms, seats, outdoor, gates, actualCapacity, complexityId, eventGuests, eventsPerYear, daysPerEvent, revenue }, cfg, adjustMode ? adjust : null),
    [rooms, seats, outdoor, gates, actualCapacity, complexityId, eventGuests, eventsPerYear, daysPerEvent, revenue, cfg, adjustMode, adjust]);
  const baseline = useMemo(() => (adjustMode && hasAdjust
    ? computeStaffing({ rooms, seats, outdoor, gates, actualCapacity, complexityId, eventGuests, eventsPerYear, daysPerEvent, revenue }, cfg)
    : null), [rooms, seats, outdoor, gates, actualCapacity, complexityId, eventGuests, eventsPerYear, daysPerEvent, revenue, cfg, adjustMode, hasAdjust]);
  const hasInputs = N(rooms) > 0 || N(seats) > 0 || N(outdoor) > 0 || N(gates) > 0;

  const setOv = (key, field, value) => {
    if (key.startsWith("added:")) {
      const id = key.slice(6);
      setAdjust((a) => ({ ...a, added: a.added.map((r) => (r.id === id ? { ...r, [field === "salary" ? "salary" : "count"]: value } : r)) }));
    } else {
      setAdjust((a) => ({ ...a, roles: { ...a.roles, [key]: { ...(a.roles[key] || {}), [field]: value } } }));
    }
  };
  const removeRole = (key) => {
    if (key.startsWith("added:")) {
      const id = key.slice(6);
      setAdjust((a) => ({ ...a, added: a.added.filter((r) => r.id !== id) }));
    } else setAdjust((a) => ({ ...a, removed: [...a.removed, key] }));
  };
  const restoreRole = (key) => setAdjust((a) => {
    const roles = { ...a.roles };
    if (roles[key] && Number(roles[key].count) === 0) { const { count, ...rest } = roles[key]; roles[key] = rest; }
    return { ...a, removed: a.removed.filter((k) => k !== key), roles };
  });
  const roleLabel = (key) => {
    const rid = key.split(":")[1];
    return (cfg.roles[rid] || (cfg.custom_roles || []).find((r) => r.id === rid) || {}).label || rid;
  };
  const hiddenKeys = [...new Set([...adjust.removed, ...Object.entries(adjust.roles).filter(([, v]) => Number(v.count) === 0 && v.count !== "").map(([k]) => k)])];

  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const payload = {
        ...guest, property_name: propertyName,
        rooms: N(rooms), seats: N(seats), outdoor: N(outdoor), gates: N(gates),
        event_capacity: res.E, capacity_estimated: res.capacityEstimated,
        complexity_label: res.complexityLabel, complexity_factor: res.factor,
        event_guests: res.Eg, events_per_year: N(eventsPerYear), days_per_event: res.event.daysPerEvent,
        departments: res.departments.map((d) => ({ label: d.label, headcount: d.headcount, monthly: d.monthly, annual: d.annual })),
        total_headcount: res.totalHeadcount, monthly_payroll: res.monthlyPayroll, annual_payroll: res.annualPayroll,
        event_roles: res.event.roles.filter((r) => r.required > 0).map((r) => ({ label: r.label, required: r.required, rate: r.rate })),
        event_required: res.event.required, deployable: res.event.deployable, additional_temp: res.event.additionalTemp,
        cost_per_event: res.event.costPerEvent, annual_temp_cost: res.event.annualTempCost,
        revenue: N(revenue), annual_total: res.finance.annualTotal,
        adjusted: adjustMode && hasAdjust,
        staffing_pct: res.finance.pct || 0, status_label: res.finance.status?.label || "",
      };
      const r = await api.post("/public/staffing-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Happy-Retreats-Staffing-Plan.pdf";
      a.click();
      URL.revokeObjectURL(url);
      trackEvent("staffing_plan_pdf", { property: propertyName, team: res.totalHeadcount });
      toast.success("Your staffing plan is downloading");
    } catch {
      toast.error("Could not prepare the PDF — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const requestDownload = () => {
    if (!hasInputs) { toast.error("Enter the property fundamentals first"); return; }
    const g = getGuest();
    if (g) downloadPdf(g);
    else setGuestOpen(true);
  };

  return (
    <div data-testid="staffing-calculator-page">
      <SEOHead title="Staffing Calculator — Plan your property's team | Happy Retreats"
        description="Happy Retreats' staffing requirement formula: the minimum permanent team to run your property, plus event-day temporary staffing — with full payroll costing and a health check against revenue." />

      <section className="bg-ink text-ivory pt-44 md:pt-52 pb-14 md:pb-20">
        <div className="container-x">
          <div className="overline text-ivory/70 animate-fade-up">{cfg.hero.overline}</div>
          <h1 className="display-1 mt-8 max-w-4xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
            {cfg.hero.headline_pre}<span className="italic text-clay">{cfg.hero.headline_accent}</span>{cfg.hero.headline_post}
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/80 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.3s" }}>{cfg.hero.subheadline}</p>
        </div>
      </section>

      <div className="container-x py-10 md:py-14">
        <div className="grid lg:grid-cols-[1fr,400px] gap-10 lg:gap-14 items-start">
          <div>
            {/* Inputs */}
            <Reveal>
              <section className="border-t border-ink/10 pt-8 pb-10">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-2xl text-clay">01</span>
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-light">{cfg.labels.inputs_title}</h2>
                    <p className="text-sm text-graphite mt-1.5 max-w-xl leading-relaxed">{cfg.labels.inputs_intro}</p>
                  </div>
                </div>
                <div className="mt-6 max-w-sm">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Property name (optional)</span>
                    <input value={propertyName} onChange={(e) => setPropertyName(e.target.value)} placeholder="e.g. Serene Meadows Estate"
                      className="mt-1.5 w-full border border-ink/15 bg-white px-3 py-2.5 outline-none text-sm focus:border-ink transition-colors" data-testid="sc-property-name" />
                  </label>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Num label="Operational rooms" value={rooms} onChange={setRooms} testId="sc-rooms" />
                  <Num label="Restaurant seats" value={seats} onChange={setSeats} testId="sc-seats" />
                  <Num label="Outdoor event area" suffix="sqft" value={outdoor} onChange={setOutdoor} testId="sc-outdoor" />
                  <Num label="Active property gates" value={gates} onChange={setGates} hint="Only gates that must stay guarded." testId="sc-gates" />
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4 max-w-xl">
                  <Num label="Actual max event capacity (optional)" value={actualCapacity} onChange={setActualCapacity}
                    hint={`Blank = estimated from outdoor area ÷ ${cfg.formulas.sqft_per_guest} sqft/guest`} testId="sc-actual-capacity" />
                  <div className="border border-ink/10 bg-white/60 px-4 py-3 self-end">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Event capacity used</div>
                    <div className="font-display text-2xl mt-1" data-testid="sc-event-capacity">{num(res.E)} guests</div>
                    <div className="text-[11px] text-graphite/70">{res.capacityEstimated ? `Estimated · ${num(N(outdoor))} sqft ÷ ${cfg.formulas.sqft_per_guest}` : "As entered by you"}</div>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium mb-2">How complex is the property to operate?</div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {(cfg.complexity.options || []).map((o) => (
                      <button key={o.id} type="button" onClick={() => setComplexityId(o.id)}
                        className={`text-left border p-3.5 transition-colors ${complexityId === o.id ? "border-ink bg-ink text-ivory" : "border-ink/15 bg-white hover:border-ink/40"}`}
                        data-testid={`sc-complexity-${o.id}`}>
                        <div className="text-sm font-medium">{o.label}</div>
                        <div className={`text-xs mt-1 leading-relaxed ${complexityId === o.id ? "text-ivory/60" : "text-graphite"}`}>{o.desc} · ×{o.factor}</div>
                      </button>
                    ))}
                  </div>
                  {res.excludedRoles.length > 0 && (
                    <p className="text-[11px] text-graphite/70 mt-2.5" data-testid="sc-excluded-note">
                      Not staffed for this property type: {res.excludedRoles.join(", ")}
                    </p>
                  )}
                </div>
              </section>
            </Reveal>

            {/* Permanent staffing */}
            {hasInputs && (
              <Reveal>
                <section className="border-t border-ink/10 pt-8 pb-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-2xl text-clay">02</span>
                    <div>
                      <h2 className="font-display text-2xl md:text-3xl font-light">{cfg.labels.perm_title}</h2>
                      <p className="text-sm text-graphite mt-1.5 max-w-xl leading-relaxed">{cfg.labels.perm_intro}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setAdjustMode(!adjustMode)}
                        className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-medium border px-4 py-2 transition-colors ${adjustMode ? "bg-ink text-ivory border-ink" : "border-ink/30 text-graphite hover:border-ink hover:text-ink"}`}
                        data-testid="sc-adjust-toggle">
                        <SlidersHorizontal className="w-3.5 h-3.5" /> {adjustMode ? "Customising — done" : "Customise this plan"}
                      </button>
                      {adjustMode && hasAdjust && (
                        <button type="button" onClick={() => { setAdjust({ roles: {}, removed: [], added: [] }); setNewRole(null); }}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-medium text-graphite/70 hover:text-ink px-2 py-2 transition-colors" data-testid="sc-adjust-reset">
                          <RotateCcw className="w-3 h-3" /> Reset to formula
                        </button>
                      )}
                    </div>
                    {adjustMode && (
                      <button type="button" onClick={() => setNewRole(newRole ? null : { label: "", salary: "", count: 1 })}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-medium border border-ink/30 text-graphite hover:border-ink hover:text-ink px-4 py-2 transition-colors" data-testid="sc-add-role-btn">
                        <Plus className="w-3.5 h-3.5" /> Add a role
                      </button>
                    )}
                  </div>
                  {adjustMode && (
                    <p className="mt-2 text-[11px] text-graphite/70">Edit counts and salaries per role, remove roles with ×, or add new ones — everything below recalculates instantly for budget iterations.</p>
                  )}
                  {adjustMode && newRole && (
                    <div className="mt-3 border border-ink/15 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="sc-add-role-form">
                      <label className="flex-1 min-w-[160px] block">
                        <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Role name</span>
                        <input value={newRole.label} onChange={(e) => setNewRole({ ...newRole, label: e.target.value })} placeholder="e.g. Pool Lifeguard"
                          className="mt-1 w-full border border-ink/15 px-3 py-2 outline-none text-sm focus:border-ink" data-testid="sc-add-role-label" />
                      </label>
                      <label className="w-24 block">
                        <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">Count</span>
                        <input type="number" min="1" value={newRole.count} onChange={(e) => setNewRole({ ...newRole, count: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) })}
                          className="mt-1 w-full border border-ink/15 px-3 py-2 outline-none text-sm focus:border-ink tabular-nums" data-testid="sc-add-role-count" />
                      </label>
                      <label className="w-32 block">
                        <span className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">₹/mo gross</span>
                        <input type="number" min="0" value={newRole.salary} onChange={(e) => setNewRole({ ...newRole, salary: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)) })}
                          className="mt-1 w-full border border-ink/15 px-3 py-2 outline-none text-sm focus:border-ink tabular-nums" data-testid="sc-add-role-salary" />
                      </label>
                      <button type="button"
                        onClick={() => {
                          if (!newRole.label.trim() || !(N(newRole.salary) > 0) || !(N(newRole.count) > 0)) { toast.error("Give the role a name, count and salary"); return; }
                          setAdjust((a) => ({ ...a, added: [...a.added, { id: `ar_${Date.now()}`, label: newRole.label.trim(), salary: N(newRole.salary), count: N(newRole.count) }] }));
                          setNewRole(null);
                        }}
                        className="bg-ink text-ivory px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] font-medium hover:bg-clay transition-colors" data-testid="sc-add-role-confirm">
                        Add to plan
                      </button>
                    </div>
                  )}
                  {adjustMode && hiddenKeys.length > 0 && (
                    <div className="mt-3 border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 flex flex-wrap items-center gap-2" data-testid="sc-removed-strip">
                      <span className="font-medium">Removed:</span>
                      {hiddenKeys.map((k) => (
                        <button key={k} type="button" onClick={() => restoreRole(k)} className="inline-flex items-center gap-1 border border-amber-300 bg-white px-2 py-0.5 rounded-full hover:border-amber-500 transition-colors" data-testid={`sc-restore-${k.replace(":", "-")}`}>
                          <span className="line-through">{roleLabel(k)}</span><span className="text-[10px] uppercase font-medium">restore</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-4">
                    <div className="flex items-center gap-3 px-4 pb-2 text-[10px] uppercase tracking-[0.14em] text-graphite font-medium">
                      <span className="flex-1">Department</span><span className="w-14 text-right">People</span>
                      <span className="w-28 text-right hidden sm:block">Monthly</span><span className="w-28 text-right hidden md:block">Annual</span><span className="w-4" />
                    </div>
                    <div className="space-y-2">
                      {res.departments.map((d) => <DeptRow key={d.id} d={d} showFormulas adjustMode={adjustMode} onOv={setOv} onRemove={removeRole} />)}
                    </div>
                    <div className="mt-4 bg-ink text-ivory px-5 py-4 flex flex-wrap items-baseline justify-between gap-3" data-testid="sc-perm-total">
                      <span className="text-sm">Estimated permanent team{res.factor !== 1 ? ` · complexity ×${res.factor}` : ""}{adjustMode && hasAdjust ? " · adjusted by you" : ""}</span>
                      <span className="font-display text-2xl">{num(res.totalHeadcount)} people · {inr(res.monthlyPayroll)}/mo</span>
                    </div>
                    {baseline && (
                      <div className="mt-2 text-[11px] text-graphite tabular-nums" data-testid="sc-baseline">
                        Formula baseline: {num(baseline.totalHeadcount)} people · {inr(baseline.monthlyPayroll)}/mo
                        {" "}→ your plan: <strong>{num(res.totalHeadcount)} people · {inr(res.monthlyPayroll)}/mo</strong>
                        {" "}({res.monthlyPayroll <= baseline.monthlyPayroll ? "saves" : "adds"} {inr(Math.abs(baseline.monthlyPayroll - res.monthlyPayroll))}/mo)
                      </div>
                    )}
                    <p className="mt-3 text-[11px] text-graphite/80">{cfg.labels.adjust_note}</p>
                  </div>
                </section>
              </Reveal>
            )}

            {/* Event staffing */}
            {hasInputs && res.E > 0 && (
              <Reveal>
                <section className="border-t border-ink/10 pt-8 pb-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-2xl text-clay">03</span>
                    <div>
                      <h2 className="font-display text-2xl md:text-3xl font-light">{cfg.labels.event_title}</h2>
                      <p className="text-sm text-graphite mt-1.5 max-w-xl leading-relaxed">{cfg.labels.event_intro}</p>
                    </div>
                  </div>
                  <div className="mt-6 grid sm:grid-cols-3 gap-4 max-w-2xl">
                    <Num label="Typical event guest count" value={eventGuests} onChange={setEventGuests} hint={`Blank = full capacity (${num(res.E)})`} testId="sc-event-guests" />
                    <Num label="Large events per year" value={eventsPerYear} onChange={setEventsPerYear} testId="sc-events-per-year" />
                    <Num label="Staffed days per event" value={daysPerEvent} onChange={setDaysPerEvent} testId="sc-days-per-event" />
                  </div>
                  <div className="mt-5 max-w-2xl bg-white border border-ink/10 p-5">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-clay font-medium mb-3">Event requirement for {num(res.Eg)} guests</div>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs text-graphite">
                      {res.event.roles.filter((r) => r.required > 0).map((r) => (
                        <div key={r.id} className="flex justify-between"><span>{r.label}</span><span className="tabular-nums">{num(r.required)}</span></div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-ink/10 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span>Total event requirement</span><span className="tabular-nums font-medium" data-testid="sc-event-required">{num(res.event.required)} people</span></div>
                      <div className="flex justify-between text-graphite"><span>Permanent staff deployable ({cfg.formulas.deployable_pct}% of operational team)</span><span className="tabular-nums" data-testid="sc-event-deployable">− {num(res.event.deployable)}</span></div>
                      <div className="flex justify-between font-medium"><span>Additional temporary staff needed</span><span className="tabular-nums" data-testid="sc-event-temp">{num(res.event.additionalTemp)} people</span></div>
                      <div className="flex justify-between"><span>Cost per event · {num(res.event.daysPerEvent)} day{res.event.daysPerEvent > 1 ? "s" : ""}</span><span className="tabular-nums" data-testid="sc-event-cost">{inr(res.event.costPerEvent)}</span></div>
                      {N(eventsPerYear) > 0 && <div className="flex justify-between"><span>Annual temporary manpower · {num(N(eventsPerYear))} events</span><span className="tabular-nums" data-testid="sc-event-annual">{inr(res.event.annualTempCost)}</span></div>}
                    </div>
                  </div>
                </section>
              </Reveal>
            )}

            {/* Health check */}
            {hasInputs && (
              <Reveal>
                <section className="border-t border-ink/10 pt-8 pb-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-2xl text-clay">04</span>
                    <div>
                      <h2 className="font-display text-2xl md:text-3xl font-light">{cfg.labels.finance_title}</h2>
                      <p className="text-sm text-graphite mt-1.5 max-w-xl leading-relaxed">Compare the annual staffing cost against your projected revenue.</p>
                    </div>
                  </div>
                  <div className="mt-6 max-w-md">
                    <Num label="Projected annual revenue" prefix="₹" value={revenue} onChange={setRevenue}
                      hint="Carried over automatically if you arrived from the revenue calculator." testId="sc-revenue" />
                  </div>
                  {res.finance.pct !== null && res.finance.status && (
                    <div className={`mt-4 max-w-md border px-4 py-3 ${TONES[res.finance.status.tone]}`} data-testid="sc-health-status">
                      <div className="text-sm font-medium">Staffing cost: {res.finance.pct.toFixed(1)}% of revenue</div>
                      <div className="text-xs mt-0.5">{res.finance.status.label}</div>
                    </div>
                  )}
                </section>
              </Reveal>
            )}
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-28 bg-ink text-ivory p-7 md:p-8" data-testid="sc-summary">
            <div className="overline text-ivory/60">{cfg.labels.summary_overline}</div>
            <h3 className="font-display text-2xl mt-3 font-light">{propertyName.trim() || "Your property"}</h3>
            {!hasInputs ? (
              <div className="mt-6 text-sm text-ivory/60 leading-relaxed" data-testid="sc-sum-empty">
                <Users className="w-5 h-5 text-clay mb-3" />
                Enter the property fundamentals — rooms, restaurant seats, outdoor area and gates — and your staffing plan will build itself here.
              </div>
            ) : (
            <>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between text-ivory/50 text-xs"><span>Event capacity</span><span className="tabular-nums">{num(res.E)} guests{res.capacityEstimated ? " (est.)" : ""}</span></div>
              <div className="flex justify-between"><span>Permanent team</span><span className="tabular-nums font-medium" data-testid="sc-sum-team">{num(res.totalHeadcount)} people</span></div>
              <div className="flex justify-between"><span>Monthly permanent payroll</span><span className="tabular-nums" data-testid="sc-sum-monthly">{inr(res.monthlyPayroll)}</span></div>
              <div className="flex justify-between"><span>Annual permanent payroll</span><span className="tabular-nums">{inr(res.annualPayroll)}</span></div>
              {res.event.annualTempCost > 0 && <div className="flex justify-between"><span>Annual event manpower</span><span className="tabular-nums">{inr(res.event.annualTempCost)}</span></div>}
            </div>
            <div className="mt-6 border-t border-ivory/15 pt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-clay font-medium">Total annual staffing cost</div>
              <div className="font-display text-4xl mt-2" data-testid="sc-total">{inr(res.finance.annualTotal)}</div>
              {res.finance.pct !== null && (
                <div className="text-xs text-ivory/60 mt-2" data-testid="sc-sum-pct">
                  {res.finance.pct.toFixed(1)}% of projected revenue
                  {res.finance.status && <span className={`ml-2 inline-block px-2 py-0.5 rounded border text-[10px] ${TONES[res.finance.status.tone]}`}>{res.finance.status.key.toUpperCase()}</span>}
                </div>
              )}
            </div>
            </>
            )}
            <button type="button" onClick={requestDownload} disabled={downloading}
              className="mt-7 w-full bg-ivory text-ink px-6 py-4 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 hover:bg-clay hover:text-white transition-colors disabled:opacity-60"
              data-testid="sc-download-pdf">
              <FileDown className="w-4 h-4" /> {downloading ? "Preparing PDF…" : cfg.labels.download_label}
            </button>
            <Link to="/partner-with-us#enquire"
              className="mt-3 w-full border border-ivory/30 text-ivory px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 hover:bg-ivory/10 transition-colors"
              data-testid="sc-partner-link">
              <Handshake className="w-4 h-4" /> {cfg.labels.discuss_label}
            </Link>
            <p className="mt-4 text-[10px] text-ivory/40 leading-relaxed">{cfg.labels.disclaimer}</p>
          </aside>
        </div>
      </div>

      {cfg.closing.enabled !== false && (
        <section className="bg-bone">
          <div className="container-x py-12 md:py-16 flex flex-wrap items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-light">{cfg.closing.heading}</h3>
              <p className="text-sm text-graphite mt-2 max-w-lg">{cfg.closing.text}</p>
            </div>
            <Link to="/partner-with-us" className="btn-primary inline-flex items-center gap-2" data-testid="sc-cta-partner">
              {cfg.closing.cta_label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      <GuestDetailsDialog
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
        heading="Almost there — who is this staffing plan for?"
        ctaLabel="Get my staffing plan"
      />
    </div>
  );
}

export default function StaffingCalculator() {
  const { content, loading } = usePageContent("staffing_calculator");
  if (loading) return <div className="pt-44 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;
  return <CalculatorBody cfg={mergeST(content)} />;
}
